import { ChevronRightIcon } from "@heroicons/react/20/solid";
import { useContext, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useLocation, useParams } from "react-router-dom";
import { BookContext } from "../../contexts/BookContext";
import { Locale } from "../../types/models/Language";

type PagesStructureItem = {
  id: string;
  name: string;
  to: string;
  current: boolean;
  parent: PagesStructureItem["id"] | null;
};

export default function Breadcrumb() {
  const location = useLocation();
  const { t } = useTranslation();
  const local = localStorage.getItem("i18nextLng") as Locale;
  const { chapters, questions, generated_chapters } = useContext(BookContext);
  const { chapterNum, questionNum } = useParams();
  const pathname = location.pathname;
  const [pages, setPages] = useState<PagesStructureItem[]>([]);
  const [isChapter, setIsChapter] = useState(false);

  const pagesStructure: PagesStructureItem[] = useMemo(() => {
    const chapterItem = chapters.find((ch) => ch.number === Number(chapterNum));
    const chapterId = chapterItem?.id || 0;
    const questionItem = questions.find(
      (q) => q.number === Number(questionNum) && q.chapter_id === chapterId
    );
    const generatedChapterItem = generated_chapters.find(
      (ch) => ch.chapter_id === chapterId
    );

    return [
      {
        id: "dashboard",
        name: t("breadcrumb.dashboard"),
        to: "/",
        current: false,
        parent: null,
      },
      ...(isChapter
        ? [
            {
              id: "chapter",
              name: chapterItem?.content[local] || t("breadcrumb.chapter"),
              to: `/chapter/${chapterNum}`,
              current: false,
              parent: "dashboard",
            },
          ]
        : [
            {
              id: "generated_chapter",
              name: generatedChapterItem?.title || t("breadcrumb.chapter"),
              to: `/generated_chapter/${chapterNum}`,
              current: true,
              parent: "dashboard",
            },
          ]),
      ...(questionItem
        ? [
            {
              id: "question",
              name: `${t("breadcrumb.question")}: ${
                questionItem.content[local] || t("breadcrumb.question")
              }`,
              to: `/chapter/${chapterNum}/question/${questionNum}`,
              current: false,
              parent: "chapter",
            },
          ]
        : []),
    ];
  }, [
    t,
    chapterNum,
    questionNum,
    questions,
    chapters,
    generated_chapters,
    local,
  ]);

  useEffect(() => {
    const pathSegments = pathname.split("/").filter(Boolean);

    let currentPageId = "dashboard";
    if (pathSegments[0] === "chapter") {
      setIsChapter(true);
      currentPageId = pathSegments[2] === "question" ? "question" : "chapter";
    } else {
      setIsChapter(false);
      currentPageId =
        pathSegments[0] === "generated_chapter"
          ? "generated_chapter"
          : "dashboard";
    }

    const currentPage = pagesStructure.find(
      (page) => page.id === currentPageId
    );

    if (!currentPage) return;

    const breadcrumb: PagesStructureItem[] = [];
    let page: PagesStructureItem | undefined = currentPage;

    while (page) {
      breadcrumb.unshift({ ...page, current: page.id === currentPage.id });
      page = pagesStructure.find(
        (parentPage) => parentPage.id === page?.parent
      );
    }

    setPages(breadcrumb);
  }, [pathname, pagesStructure]);

  return (
    <nav aria-label="Breadcrumb" className="flex w-full overflow-x-auto">
      <ol
        role="list"
        className="flex items-center space-x-2 sm:space-x-4 min-w-max"
      >
        {pages.map((page, index) => (
          <li key={page.id}>
            <div className="flex items-center">
              {index > 0 && (
                <ChevronRightIcon
                  aria-hidden="true"
                  className="w-4 h-4 sm:w-5 sm:h-5 shrink-0 text-slate-50"
                />
              )}
              <a
                href={page.to}
                aria-current={page.current ? "page" : undefined}
                className={`${
                  index > 0 ? "ml-2 sm:ml-4" : ""
                } text-xs sm:text-sm font-medium truncate max-w-[120px] sm:max-w-none ${
                  page.current
                    ? "text-delicate-700 hover:text-delicate-900"
                    : "text-slate-50 hover:text-slate-900"
                }`}
                title={page.name}
              >
                {page.name}
              </a>
            </div>
          </li>
        ))}
      </ol>
    </nav>
  );
}
